// Typed wrapper around fetch. Same-origin, expects the Vite dev proxy
// (or the Go stack in production) to handle /api/*.

// CSRF: ab Slice 2 erwartet das Backend einen X-CSRF-Token-Header auf
// allen state-changing Requests an /v2-api/*. Der Wert kommt aus dem
// __Host-csrf-Cookie (vom Login gesetzt, JS-lesbar). Wenn das Cookie
// fehlt (vor dem ersten Login), schicken wir den Header nicht --
// Login-Endpoint selbst ist von der CSRF-Pruefung ausgenommen.
const CSRF_COOKIE_NAME = "__Host-csrf";
const CSRF_HEADER_NAME = "X-CSRF-Token";

function readCookie(name: string): string | null {
  const all = document.cookie || "";
  for (const part of all.split("; ")) {
    const eq = part.indexOf("=");
    if (eq <= 0) continue;
    if (part.slice(0, eq) === name) {
      return decodeURIComponent(part.slice(eq + 1));
    }
  }
  return null;
}

function csrfHeader(): Record<string, string> {
  const v = readCookie(CSRF_COOKIE_NAME);
  return v ? { [CSRF_HEADER_NAME]: v } : {};
}

class APIError extends Error {
  /** Der Satz des Servers OHNE den angehängten Pfad. `message` trägt den
   *  Pfad für die Fehlersuche; wo der Server einen Satz für Menschen
   *  schickt („Haltezeit: eine ganze Zahl von 0 bis 300"), zeigt die
   *  Oberfläche `detail`. */
  detail: string;
  constructor(public status: number, message: string, detail?: string) {
    super(message);
    this.name = "APIError";
    this.detail = detail ?? message;
  }
}

async function parseError(r: Response, path: string): Promise<APIError> {
  // Best-effort detail extraction: Python's FastAPI often returns
  // {detail: "…"} on errors. Fall back to the status line.
  let detail = "";
  let stufe = false;
  try {
    const j = await r.json();
    if (typeof j === "object" && j !== null) {
      stufe = (j as Record<string, unknown>).stufe === true;
      const d = (j as Record<string, unknown>).detail;
      if (typeof d === "string") detail = d;
      else if (typeof (j as Record<string, unknown>).error === "string") {
        detail = (j as Record<string, unknown>).error as string;
      }
    }
  } catch {
    // not JSON
  }
  // 423 heisst beim Vault immer dasselbe: der PIN-Schutz ist aktiv und
  // die Sitzung nicht entsperrt. Das ist kein technischer Fehler,
  // sondern ein Zustand -- und "423 (/v2-api/vault/versions?path=…)"
  // sagt niemandem etwas (Tim 2026-07-29).
  if (r.status === 423) {
    // "stufe": die Aktion liegt hinter der Schutzstufe
    // (internal/api/schutzstufe.go) und die Seite hat kein eigenes Tor --
    // dann zeigt PinAnfrage.svelte die Eingabe. Ohne "stufe" kümmert sich
    // die Seite selbst (Vault, Zeitreise, Dateien, Terminal, Ablage).
    // Die Eingabe selbst öffnet lib/pin-stufe.ts (an fetch) -- hier nur
    // die passende Meldung.
    if (stufe) {
      return new APIError(423, "Das verlangt die Vault-PIN.");
    }
    return new APIError(423, "Der Vault ist gesperrt — bitte im Vault-Browser den PIN eingeben.");
  }
  const msg = detail || `${r.status} ${r.statusText}`;
  return new APIError(r.status, `${msg} (${path})`, msg);
}

async function getJSON<T>(path: string, signal?: AbortSignal): Promise<T> {
  const r = await fetch(path, {
    credentials: "include",
    headers: { Accept: "application/json" },
    signal,
  });
  if (!r.ok) throw await parseError(r, path);
  return (await r.json()) as T;
}

async function postJSON<T>(
  path: string,
  body?: unknown,
  signal?: AbortSignal,
): Promise<T> {
  const r = await fetch(path, {
    method: "POST",
    credentials: "include",
    headers: {
      Accept: "application/json",
      ...(body !== undefined ? { "Content-Type": "application/json" } : {}),
      ...csrfHeader(),
    },
    body: body !== undefined ? JSON.stringify(body) : undefined,
    signal,
  });
  if (!r.ok) throw await parseError(r, path);
  // Some endpoints (POST /api/...) return 204 / empty body.
  const text = await r.text();
  if (!text) return undefined as T;
  return JSON.parse(text) as T;
}

async function deleteJSON<T>(path: string, signal?: AbortSignal): Promise<T> {
  const r = await fetch(path, {
    method: "DELETE",
    credentials: "include",
    headers: { Accept: "application/json", ...csrfHeader() },
    signal,
  });
  if (!r.ok) throw await parseError(r, path);
  const text = await r.text();
  if (!text) return undefined as T;
  return JSON.parse(text) as T;
}

// patchJSON / putJSON: gleiches Schema wie postJSON, fuer Endpoints
// die semantisch ein Update sind (Settings-PATCH, Resource-PUT). Beide
// gehen ueber die CSRF-geschuetzte fetch-Bahn.
async function patchJSON<T>(
  path: string,
  body?: unknown,
  signal?: AbortSignal,
): Promise<T> {
  return mutateJSON<T>("PATCH", path, body, signal);
}

async function putJSON<T>(
  path: string,
  body?: unknown,
  signal?: AbortSignal,
): Promise<T> {
  return mutateJSON<T>("PUT", path, body, signal);
}

async function mutateJSON<T>(
  method: "PATCH" | "PUT",
  path: string,
  body: unknown,
  signal?: AbortSignal,
): Promise<T> {
  const r = await fetch(path, {
    method,
    credentials: "include",
    headers: {
      Accept: "application/json",
      ...(body !== undefined ? { "Content-Type": "application/json" } : {}),
      ...csrfHeader(),
    },
    body: body !== undefined ? JSON.stringify(body) : undefined,
    signal,
  });
  if (!r.ok) throw await parseError(r, path);
  const text = await r.text();
  if (!text) return undefined as T;
  return JSON.parse(text) as T;
}

export {
  APIError,
  csrfHeader,
  deleteJSON,
  getJSON,
  patchJSON,
  postJSON,
  putJSON,
};
